import { cryptoStorage } from "../utils/cryptoStorage";
import Logger from "../utils/logger";
import {
  AuthToken,
  JwtToken,
  SessionToken,
  RefreshToken,
  EmptyToken,
} from "../models/token.model";
import { Role, UiMode } from "../models/static.model";

class DataService {
  private SESSION_TOKEN_KEY: string = "NPU_SESSION_TOKEN";
  private REFRESH_TOKEN_KEY: string = "NPU_REFRESH_TOKEN";
  private ROLE: string = "NPU_ROLE";
  private UI_MODE: string = "NPU_UI_MODE";
  private LOGIN_TS: string = "NPU_LOGIN_TS";
  private sessionToken: AuthToken;
  private refreshToken: AuthToken;
  private role: Role;
  private uiMode: UiMode;
  private loginTs: string;

  private logger: Logger = new Logger(this.constructor.name);

  constructor(prefetchValues: Boolean = false) {
    this.sessionToken = new AuthToken(new EmptyToken());
    this.refreshToken = new AuthToken(new EmptyToken());
    this.role = Role.Empty;
    this.uiMode = UiMode.Web;
    this.loginTs = new Date().getTime().toString();
    if (prefetchValues) {
      this.getSessionToken();
      this.getRefreshToken();
      this.getRole();
      this.getLoginTs();
    }
  }


  setLoginTs(): Promise<string> {
    const ts = new Date().getTime();
    return cryptoStorage.setItem(this.LOGIN_TS, new Date(ts).toLocaleString()).then(() => {
      this.loginTs = new Date(ts).toLocaleString();
      return this.loginTs;
    });
  }

  getLoginTs(): Promise<string> {
    return cryptoStorage.getItem(this.LOGIN_TS).then((ts: string) => {
      return this.loginTs = ts
    })
  }

  setUiMode(mode: string): Promise<UiMode> {
    if (mode === "web") {
      return cryptoStorage.setItem(this.UI_MODE, mode).then(() => {
        this.logger.debug("ui mode has been set:", mode);
        this.uiMode = UiMode.Web;
        return this.uiMode;
      });
    } else if (mode === "web-view") {
      return cryptoStorage.setItem(this.UI_MODE, mode).then(() => {
        this.logger.debug("ui mode has been set:", mode);
        this.uiMode = UiMode.WebView;
        return this.uiMode;
      });
    } else {
      return cryptoStorage.setItem(this.UI_MODE, mode).then(() => {
        this.logger.debug("ui mode has been set:", mode);
        this.uiMode = UiMode.Web;
        return this.uiMode;
      });
    }
  }

  getUiMode(): Promise<UiMode> {
    return cryptoStorage.getItem(this.UI_MODE).then((mode: string) => {
      if (mode === "web") {
        this.logger.debug("ui mode found:", mode);
        this.uiMode = UiMode.Web;
        return this.uiMode;
      } else if (mode === "web-view") {
        this.logger.debug("ui mode found:", mode);
        this.uiMode = UiMode.WebView;
        return this.uiMode;
      } else if (mode === null) {
        this.logger.debug("ui mode not found");
        this.uiMode = UiMode.Web;
        return this.uiMode;
      } else {
        this.logger.debug("ui mode found but unknown:", mode);
        this.uiMode = UiMode.Web;
        return this.uiMode;
      }
    });
  }

  getUiModeSync(): UiMode {
    const mode = cryptoStorage.getItemSync(this.UI_MODE);
    if (mode === "web") {
      this.logger.debug("ui mode found:", mode);
      this.uiMode = UiMode.Web;
      return this.uiMode;
    } else if (mode === "web-view") {
      this.logger.debug("ui mode found:", mode);
      this.uiMode = UiMode.WebView;
      return this.uiMode;
    } else if (mode === null) {
      this.logger.debug("ui mode not found");
      this.uiMode = UiMode.Web;
      return this.uiMode;
    } else {
      this.logger.debug("ui mode found but unknown:", mode);
      this.uiMode = UiMode.Web;
      return this.uiMode;
    }
  }

  setRole(role: string): Promise<Role> {
    if (role === "admin") {
      return cryptoStorage.setItem(this.ROLE, role).then(() => {
        this.logger.debug("Admin role has been set:", role);
        this.role = Role.Admin;
        return this.role;
      });
    } else if (role === "user") {
      return cryptoStorage.setItem(this.ROLE, role).then(() => {
        this.logger.debug("User role has been set:", role);
        this.role = Role.User;
        return this.role;
      });
    } else {
      return cryptoStorage.setItem(this.ROLE, role).then(() => {
        this.logger.debug("unknown role has been set:", role);
        this.role = Role.Unknown;
        return this.role;
      });
    }
  }

  getRole(): Promise<Role> {
    if (this.role === Role.Empty) {
      return cryptoStorage.getItem(this.ROLE).then((role: string) => {
        if (role === "admin") {
          this.logger.debug("role found:", role);
          this.role = Role.Admin;
          return this.role;
        } else if (role === "user") {
          this.logger.debug("role found:", role);
          this.role = Role.User;
          return this.role;
        } else if (role === null) {
          this.logger.debug("role not found");
          this.role = Role.Empty;
          return this.role;
        } else {
          this.logger.debug("role found bun unknown:", role);
          this.role = Role.Unknown;
          return this.role;
        }
      });
    } else {
      return Promise.resolve(this.role);
    }
  }

  getRoleSync(): Role {
    if (this.role === Role.Empty) {
      const role: string = cryptoStorage.getItemSync(this.ROLE);
      if (role === "admin") {
        this.logger.debug("role found:", role);
        this.role = Role.Admin;
        return this.role;
      } else if (role === "user") {
        this.logger.debug("role found:", role);
        this.role = Role.User;
        return this.role;
      } else if (role === null) {
        this.logger.debug("role not found");
        this.role = Role.Empty;
        return this.role;
      } else {
        this.logger.debug("role found bun unknown:", role);
        this.role = Role.Unknown;
        return this.role;
      }
    } else {
      return this.role;
    }
  }

  setSessionToken(token: JwtToken): Promise<AuthToken> {
    return cryptoStorage.setItem(this.SESSION_TOKEN_KEY, token).then(() => {
      this.logger.debug("session token has been set:", token);
      this.sessionToken = new AuthToken(new SessionToken(), token);
      return this.sessionToken;
    });
  }

  getSessionTokenSync(): AuthToken {
    if (this.sessionToken.tokenType instanceof EmptyToken) {
      const token: any = cryptoStorage.getItemSync(this.SESSION_TOKEN_KEY);
      if (token === null) {
        this.logger.warn("(sync) session token not found");
        return new AuthToken(new EmptyToken());
      } else {
        this.logger.debug("(sync) session token found:", token);
        this.sessionToken = new AuthToken(new SessionToken(), token);
        return this.sessionToken;
      }
    } else {
      return this.sessionToken;
    }
  }

  getSessionToken(): Promise<AuthToken> {
    if (this.sessionToken.tokenType instanceof EmptyToken) {
      return cryptoStorage
        .getItem(this.SESSION_TOKEN_KEY)
        .then((token: any) => {
          if (token === null) {
            this.logger.warn("session token not found");
            return new AuthToken(new EmptyToken());
          } else {
            this.logger.debug("session token found:", token);
            this.sessionToken = new AuthToken(new SessionToken(), token);
            return this.sessionToken;
          }
        });
    } else {
      return Promise.resolve(this.sessionToken);
    }
  }

  setRefreshToken(token: JwtToken): Promise<AuthToken> {
    return cryptoStorage.setItem(this.REFRESH_TOKEN_KEY, token).then(() => {
      this.logger.debug("refresh token has been set:", token);
      this.refreshToken = new AuthToken(new RefreshToken(), token);
      return this.refreshToken;
    });
  }

  getRefreshToken(): Promise<AuthToken> {
    if (this.refreshToken.tokenType instanceof EmptyToken) {
      return cryptoStorage
        .getItem(this.REFRESH_TOKEN_KEY)
        .then((token: any) => {
          if (token === null) {
            this.logger.warn("refresh token not found");
            return new AuthToken(new EmptyToken());
          } else {
            this.logger.debug("refresh token found:", token);
            this.refreshToken = new AuthToken(new RefreshToken(), token);
            return this.refreshToken;
          }
        });
    } else {
      return Promise.resolve(this.refreshToken);
    }
  }

  clear(): Promise<void> {
    return cryptoStorage.clear().then(() => {
      // this.sessionToken = new AuthToken(new EmptyToken());
      // this.refreshToken = new AuthToken(new EmptyToken());
      this.role = Role.Empty;
      // localStorageAsync.setItem("i18nextLng", "ua");
      this.logger.debug("all data has been cleared");
    });
  }
}

export const dataService = new DataService(true);
